import type { JSX } from 'react';
import type { Id, SceneItem } from '../../core/model';
import { Icon } from '../components/Icon';
import { MediaSeek } from '../dialogs/properties/MediaTransport';
import { useActions, useEditedSceneId, useProject, useRuntime, useStudio, useUi } from '../context';
import { formatDuration } from '../format';
import { safely } from '../safe';

function findItem(items: readonly SceneItem[], id: Id): SceneItem | null {
  for (const item of items) {
    if (item.id === id) return item;
    const inside = item.type === 'group' ? findItem(item.children, id) : null;
    if (inside) return inside;
  }
  return null;
}

/** Play/pause, stop, position and time of a source with `caps.controllableMedia`. */
function MediaControls({ sourceId }: { sourceId: Id }): JSX.Element {
  const studio = useStudio();
  const media = useRuntime((r) => r.sources[sourceId]?.media);
  const run = (action: string): void => void safely(studio, () => studio.invokeSource(sourceId, action), 'The source did not respond');
  const playing = media?.state === 'playing' || media?.state === 'buffering';
  return (
    <>
      <button type="button" className="ui-btn" aria-label={playing ? 'Pause' : 'Play'} title={playing ? 'Pause' : 'Play'} onClick={() => run(playing ? 'pause' : 'play')}>
        <Icon name={playing ? 'pause' : 'play'} size={14} />
      </button>
      <button type="button" className="ui-btn" aria-label="Stop" title="Stop" onClick={() => run('stop')}>
        <Icon name="stop" size={14} />
      </button>
      <div className="source-toolbar-seek">
        <MediaSeek sourceId={sourceId} />
      </div>
      {/* Elapsed, then what is left as a negative time. */}
      <span className="ui-mono" role="status" aria-label="Playback time">
        {media ? `${formatDuration(media.timeMs)}${media.durationMs > 0 ? ` / -${formatDuration(media.durationMs - media.timeMs)}` : ''}` : '—'}
      </span>
    </>
  );
}

/** Under the picture: the properties and filters of the one selected source, and the transport when it plays media. */
export function SourceToolbar(): JSX.Element {
  const studio = useStudio();
  const actions = useActions();
  const sceneId = useEditedSceneId();
  const selection = useUi((s) => s.selection);
  const only = selection.length === 1 ? selection[0]! : null;
  const sourceId = useProject((p) => {
    const item = only && sceneId ? findItem(p.scenes[sceneId]?.items ?? [], only) : null;
    return item?.type === 'source' ? item.sourceId : null;
  });
  const kind = useProject((p) => (sourceId ? p.sources[sourceId]?.kind : undefined));
  const media = kind ? studio.registry.getSource(kind)?.caps.controllableMedia === true : false;
  return (
    <div className="source-toolbar" role="toolbar" aria-label="Selected source">
      {only && sourceId ? (
        <>
          <button type="button" className="ui-btn" onClick={() => actions.openProperties(only)}>
            <Icon name="gear" size={14} /> Properties
          </button>
          <button type="button" className="ui-btn" onClick={() => actions.openFilters(only)}>
            <Icon name="sliders" size={14} /> Filters
          </button>
          {media ? <MediaControls sourceId={sourceId} /> : null}
        </>
      ) : (
        <span className="ui-dim">No source selected</span>
      )}
    </div>
  );
}
